import React, { useEffect, useMemo, useState } from "react";
import { brand } from "../../theme/theme";
import {
  Box,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Search,
  ArrowRight,
  Clock,
} from "lucide-react";

import { UnifiedModal } from "../../components/UnifiedModal";
import { Button } from "../../components/Button";
import { CustomTextField } from "../../components/CustomTextField";
import { CustomSelector } from "../../components/CustomSelector";
import { Badge } from "../../components/Badge";
import { Avatar, AvatarFallback } from "../../components/Avatar";
import apiClient from "../../api-client";

const PAGE_SIZE = 10;
const ANY_OPTION_VALUE = "__all__";
// Referencias estables: un `{}`/`[]` como default de props cambia en cada render y dispara los useEffect
const EMPTY_FILTERS = {};
const EMPTY_CLIENTS = [];

const normalizeThreadsResponse = (data = []) => {
  if (!data) return [];

  const list = Array.isArray(data)
    ? data
    : Array.isArray(data?.results)
      ? data.results
      : [];

  return list
    .map((thread) => ({
      ...thread,
      title:
        thread.type === "general"
          ? "Conversación principal"
          : thread.title || "Sin título",
    }))
    .sort((a, b) => {
      if (a.type === "general") return -1;
      if (b.type === "general") return 1;
      return 0;
    });
};

const normalizeParticipants = (data = []) => {
  const list = Array.isArray(data) ? data : [];
  return list.map((participant) => ({
    ...participant,
    fullName:
      participant.full_name ||
      [participant.first_name, participant.last_name]
        .filter(Boolean)
        .join(" ") ||
      participant.email ||
      "Participante sin nombre",
  }));
};

const collectParticipantsFromThreads = (threads = []) => {
  const map = {};
  threads.forEach((thread) => {
    if (!thread?.participants) return;
    normalizeParticipants(thread.participants).forEach((participant) => {
      const key = String(participant.id ?? participant.email ?? participant.fullName);
      if (!map[key]) {
        map[key] = participant;
      }
    });
  });
  return Object.values(map);
};

const formatDateTime = (iso) => {
  if (!iso) return "";
  try {
    const date = new Date(iso);
    return date.toLocaleString();
  } catch (err) {
    return iso;
  }
};

const extractSearchError = (error) => {
  if (!error) return "";
  if (error.response) {
    const { status, data } = error.response;
    if (status === 400) {
      if (typeof data === "string") return data;
      if (data?.detail) return data.detail;
      return "Introduce un término de búsqueda.";
    }
    if (status === 403)
      return "No tienes permisos para realizar esta búsqueda.";
    if (status === 404) return "No se encontró la información solicitada.";
  }

  return "No se pudo completar la búsqueda. Inténtalo nuevamente.";
};

export function SearchMessagesModal({
  open,
  onClose,
  defaultFilters = EMPTY_FILTERS,
  clients = EMPTY_CLIENTS,
  currentUser,
  onResultSelect,
  lockClientSelector = false,
  // Canal interno: sin clientes ni filtros, se busca en todos los hilos internos de la agencia
  internal = false,
}) {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedThread, setSelectedThread] = useState(null);
  const [selectedAuthor, setSelectedAuthor] = useState(null);

  const [threadsByClient, setThreadsByClient] = useState({});
  const [participantsByClient, setParticipantsByClient] = useState({});

  const [isLoadingThreads, setIsLoadingThreads] = useState(false);
  const [isLoadingParticipants, setIsLoadingParticipants] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  const [searchError, setSearchError] = useState(null);
  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);

  const [defaultsApplied, setDefaultsApplied] = useState(false);

  const clientOptions = useMemo(() => {
    let options = [
      {
        value: ANY_OPTION_VALUE,
        label: "Mostrar todos",
      },
    ];

    // Single-client user: mostrar solo su empresa
    if (currentUser?.role === "client" && currentUser?.client && clients.length <= 1) {
      const clientFromList = clients.find(
        (client) => String(client.id) === String(currentUser.client)
      );

      options.push({
        value: currentUser.client,
        label: clientFromList?.name || "Mi empresa",
        raw: clientFromList,
      });
    } else {
      // Multi-client user o roles no-client: mostrar toda la lista
      options.push(
        ...clients.map((client) => ({
          value: client.id,
          label: client.name,
          raw: client,
        }))
      );
    }

    return options;
  }, [clients, currentUser]);

  const threadOptions = useMemo(() => {
    if (!selectedClient) return [];
    const threads = threadsByClient[selectedClient] || [];
    return [
      {
        value: ANY_OPTION_VALUE,
        label: "Mostrar todos",
      },
      ...threads.map((thread) => ({
        value: thread.id,
        label: thread.title,
        raw: thread,
      })),
    ];
  }, [selectedClient, threadsByClient]);

  const participantOptions = useMemo(() => {
    if (!selectedClient) return [];
    const participants = participantsByClient[selectedClient] || [];
    return [
      {
        value: ANY_OPTION_VALUE,
        label: "Mostrar todos",
      },
      ...participants.map((participant) => ({
        value: participant.id,
        label: participant.fullName,
        raw: participant,
      })),
    ];
  }, [participantsByClient, selectedClient]);

  useEffect(() => {
    if (!open) return;

    const defaults = defaultFilters || {};
    setSearchTerm(defaults.q || "");

    const inferredClient = internal
      ? null
      : defaults.client || currentUser?.client || null;
    setSelectedClient(inferredClient);
    setSelectedThread(defaults.thread || null);
    setSelectedAuthor(defaults.author || null);

    setResults([]);
    setSearchError(null);
    setDefaultsApplied(false);
    setShowResults(false);
  }, [open, defaultFilters, currentUser, internal]);

  useEffect(() => {
    if (!open) return;
    if (!selectedClient) {
      setSelectedThread(null);
      setSelectedAuthor(null);
      return;
    }

    const threads = threadsByClient[selectedClient];
    if (
      selectedThread &&
      threads &&
      !threads.some((thread) => String(thread.id) === String(selectedThread))
    ) {
      setSelectedThread(null);
    }
  }, [open, selectedClient, selectedThread, threadsByClient]);

  useEffect(() => {
    if (!open) return;
    if (!selectedClient) return;
    if (threadsByClient[selectedClient]) {
      return;
    }

    let cancelled = false;
    const fetchThreads = async () => {
      try {
        setIsLoadingThreads(true);
        setIsLoadingParticipants(true);
        const response = await apiClient.get(`communication/threads/`, {
          params: { client: selectedClient },
        });
        if (cancelled) return;
        const normalized = normalizeThreadsResponse(response.data);
        setThreadsByClient((prev) => ({
          ...prev,
          [selectedClient]: normalized,
        }));
        const participants = collectParticipantsFromThreads(normalized);
        setParticipantsByClient((prev) => ({
          ...prev,
          [selectedClient]: participants,
        }));
      } catch (error) {
        console.error(
          "Error fetching threads for client",
          selectedClient,
          error
        );
      } finally {
        if (!cancelled) {
          setIsLoadingThreads(false);
          setIsLoadingParticipants(false);
        }
      }
    };

    fetchThreads();

    return () => {
      cancelled = true;
    };
  }, [open, selectedClient, threadsByClient]);

  useEffect(() => {
    if (!open) return;
    if (!selectedClient) return;

    const cachedThreads = threadsByClient[selectedClient];
    if (cachedThreads && !participantsByClient[selectedClient]) {
      const participants = collectParticipantsFromThreads(cachedThreads);
      setParticipantsByClient((prev) => ({
        ...prev,
        [selectedClient]: participants,
      }));
      return;
    }

    if (participantsByClient[selectedClient]) {
      const participants = participantsByClient[selectedClient];
      if (
        selectedAuthor &&
        !participants.some(
          (participant) => String(participant.id) === String(selectedAuthor)
        )
      ) {
        setSelectedAuthor(null);
      }
      return;
    }
  }, [
    open,
    selectedClient,
    selectedAuthor,
    participantsByClient,
    threadsByClient,
  ]);

  useEffect(() => {
    if (!open) return;
    if (!selectedClient) return;
    if (!threadsByClient[selectedClient]) return;
    if (defaultsApplied) return;

    const defaults = defaultFilters || {};

    if (defaults.thread) {
      const matches = threadsByClient[selectedClient]?.some(
        (thread) => String(thread.id) === String(defaults.thread)
      );
      if (!matches) {
        setSelectedThread(null);
      }
    }

    setDefaultsApplied(true);
  }, [open, selectedClient, threadsByClient, defaultFilters, defaultsApplied]);

  const handleClose = () => {
    if (onClose) {
      onClose(false);
    }
  };

  const handleSearch = async () => {
    const term = searchTerm.trim();
    if (!term) {
      setSearchError("Introduce un término de búsqueda.");
      setResults([]);
      setShowResults(false);
      return;
    }

    try {
      setIsSearching(true);
      setSearchError(null);
      setShowResults(true);

      const params = {
        q: term,
      };

      if (internal) {
        params.internal = true;
      } else {
        if (selectedClient) {
          params.client = selectedClient;
        }
        if (selectedThread) {
          params.thread = selectedThread;
        }
        if (selectedAuthor) {
          params.author = selectedAuthor;
        }
      }

      const response = await apiClient.get("communication/search/messages/", {
        params,
      });

      const data = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.results)
          ? response.data.results
          : [];

      setResults(data);

      if (data.length === 0) {
        // No establecemos searchError aquí para evitar el box rojo redundante
        // El mensaje se mostrará en el componente de "No se encontraron resultados"
      }
    } catch (error) {
      console.error("Error searching messages", error);
      setResults([]);
      setSearchError(extractSearchError(error));
    } finally {
      setIsSearching(false);
    }
  };

  const handleResultClick = (result) => {
    if (!onResultSelect) return;
    const position = Number(result.message_position) || 0;
    const page = Math.max(1, Math.floor(position / PAGE_SIZE) + 1);

    onResultSelect({
      result,
      page,
      filters: {
        client: selectedClient,
        thread: selectedThread,
        author: selectedAuthor,
        q: searchTerm,
      },
    });

    handleClose();
  };

  // Solo deshabilitar el selector para single-client users (no multi-client)
  const disableClientSelector =
    currentUser?.role === "client" && !!currentUser?.client && clients.length <= 1;

  return (
    <UnifiedModal
      open={open}
      onClose={handleClose}
      title={internal ? "Buscar en el canal interno" : "Buscar mensajes"}
      maxWidth="md"
      contentSx={{
        display: "flex",
        flexDirection: "column",
        gap: "24px",
      }}
    >
      <Box>
        <Stack spacing={2.5}>
          <Stack spacing={1}>
            <Typography
              variant="subtitle2"
              sx={{ color: theme.palette.text.secondary }}
            >
              Término de búsqueda
            </Typography>
            <CustomTextField
              placeholder="Busca palabras clave, temas o frases"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              sx={{ flex: 1 }}
            />
          </Stack>

          {!internal && <Divider sx={{ my: 1 }} />}

          {!internal && (
          <Stack spacing={2.25}>
            <Typography
              variant="subtitle2"
              sx={{ color: theme.palette.text.secondary }}
            >
              Filtros opcionales
            </Typography>

            <Stack spacing={1.5} direction={{ xs: "column", sm: "row" }}>
              {/* Mostrar selector de cliente para:
                  - Todos los roles NO-client
                  - Clientes multi-client (clients.length > 1)
              */}
              {(currentUser?.role !== "client" || clients.length > 1) && (
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      marginBottom: "6px",
                    }}
                  >
                    {currentUser?.role === "client" ? "Empresa" : "Cliente"}
                  </Typography>
                  <CustomSelector
                    value={selectedClient === null ? ANY_OPTION_VALUE : selectedClient}
                    onChange={(value) => {
                      if (value === ANY_OPTION_VALUE) {
                        setSelectedClient(null);
                      } else {
                        setSelectedClient(value);
                      }
                    }}
                    options={clientOptions}
                    placeholder="Mostrar todos"
                    disabled={
                      lockClientSelector ||
                      disableClientSelector ||
                      clientOptions.length === 0
                    }
                    searchable
                    placeholderValues={[ANY_OPTION_VALUE]}
                  />
                </Box>
              )}

              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    marginBottom: "6px",
                  }}
                >
                  Conversación
                </Typography>
                <CustomSelector
                  value={selectedThread === null ? ANY_OPTION_VALUE : selectedThread}
                  onChange={(value) => {
                    if (value === ANY_OPTION_VALUE) {
                      setSelectedThread(null);
                    } else {
                      setSelectedThread(value);
                    }
                  }}
                  options={threadOptions}
                  placeholder={!selectedClient ? "Selecciona primero un cliente" : "Mostrar todos"}
                  disabled={!selectedClient || isLoadingThreads}
                  searchable
                  placeholderValues={[ANY_OPTION_VALUE]}
                />
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    marginBottom: "6px",
                  }}
                >
                  Autor
                </Typography>
                <CustomSelector
                  value={selectedAuthor === null ? ANY_OPTION_VALUE : selectedAuthor}
                  onChange={(value) => {
                    if (value === ANY_OPTION_VALUE) {
                      setSelectedAuthor(null);
                    } else {
                      setSelectedAuthor(value);
                    }
                  }}
                  options={participantOptions}
                  placeholder={!selectedClient ? "Selecciona primero un cliente" : "Mostrar todos"}
                  disabled={!selectedClient || isLoadingParticipants}
                  searchable
                  placeholderValues={[ANY_OPTION_VALUE]}
                />
              </Box>
            </Stack>
          </Stack>
          )}

          {searchError && (
            <Box
              sx={{
                backgroundColor: theme.palette.error.light + "22",
                border: `1px solid ${theme.palette.error.light}55`,
                borderRadius: "8px",
                padding: "12px 16px",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.error.dark,
                }}
              >
                {searchError}
              </Typography>
            </Box>
          )}
        </Stack>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button onClick={handleSearch}>
            <Search size={16} style={{ marginRight: "8px" }} />
            Buscar
          </Button>
        </Box>
      </Box>

      {showResults && (
        <>
          <Divider sx={{ my: 1 }} />

          <Stack spacing={2.5} sx={{ flex: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{ color: theme.palette.text.secondary }}
            >
              Resultados
            </Typography>

            {isSearching && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "32px 0",
                }}
              >
                <CircularProgress size={32} />
              </Box>
            )}

            {!isSearching && results.length === 0 && (
              <Box
                sx={{
                  padding: "32px",
                  borderRadius: "12px",
                  border: `1px dashed ${theme.palette.divider}`,
                  textAlign: "center",
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  No se encontraron resultados
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.secondary, marginTop: "8px" }}
                >
                  No se encontraron mensajes que coincidan con la búsqueda.
                </Typography>
              </Box>
            )}

            {!isSearching && results.length > 0 && (
              <Stack spacing={1.5}>
                {results.map((result) => {
                  const senderName =
                    result.sender?.full_name?.trim() ||
                    [result.sender?.first_name, result.sender?.last_name]
                      .filter(Boolean)
                      .join(" ") ||
                    result.sender?.email ||
                    "Usuario";
                  const senderRoleRaw = result.sender?.role || "rol desconocido";
                  const senderRole = senderRoleRaw
                    .replace(/_/g, " ")
                    .replace(/\b\w/g, (char) => char.toUpperCase());
                  const clientFromList = clients.find(
                    (client) => String(client.id) === String(result.client_id)
                  );
                  const clientLabel =
                    (result.client_name && result.client_name.trim()) ||
                    clientFromList?.name ||
                    (result.client_id
                      ? `Cliente #${result.client_id}`
                      : "Cliente sin asignar");
                  const cachedThreads =
                    result.client_id && threadsByClient[result.client_id]
                      ? threadsByClient[result.client_id]
                      : [];
                  const threadFromCache = cachedThreads.find(
                    (thread) => String(thread.id) === String(result.thread_id)
                  );
                  const threadLabel =
                    (result.thread_title && result.thread_title.trim()) ||
                    threadFromCache?.title ||
                    (result.thread_id
                      ? `Hilo #${result.thread_id}`
                      : "Conversación sin título");
                  const contentSnippet =
                    result.content_snippet?.trim() || "Contenido no disponible";

                  return (
                    <Box
                      key={result.id}
                      onClick={() => handleResultClick(result)}
                      sx={{
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: "12px",
                        padding: "16px",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        backgroundColor: theme.palette.background.default,
                        "&:hover": {
                          boxShadow: theme.shadows[1],
                        },
                      }}
                    >
                      <Stack spacing={1.25}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                          {!internal && <Badge variant="secondary">{clientLabel}</Badge>}
                          <Badge variant="outline">{threadLabel}</Badge>
                          <Box
                            sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                          >
                            <Avatar
                              src={result.sender?.profile_image || undefined}
                              sx={{ width: 20, height: 20 }}
                            >
                              <AvatarFallback
                                sx={{
                                  backgroundColor: brand.avatar,
                                  color: "#fff",
                                  fontSize: "0.6rem",
                                  fontWeight: 600,
                                }}
                              >
                                {result.sender?.full_name || result.sender?.first_name || result.sender?.email
                                  ? (result.sender?.full_name || result.sender?.first_name || result.sender?.email)
                                    .split(" ")
                                    .map((n) => n[0])
                                    .join("")
                                    .toUpperCase()
                                    .slice(0, 2)
                                  : "U"}
                              </AvatarFallback>
                            </Avatar>
                            <Typography
                              variant="caption"
                              sx={{ color: theme.palette.text.secondary }}
                            >
                              {senderName} · {senderRole}
                            </Typography>
                          </Box>
                          <Box sx={{ flex: 1, minWidth: "20px" }} />
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              ml: "auto",
                            }}
                          >
                            <Clock size={12} color={theme.palette.text.secondary} />
                            <Typography
                              variant="caption"
                              sx={{
                                color: theme.palette.text.secondary,
                              }}
                            >
                              {formatDateTime(result.created_at)}
                            </Typography>
                          </Box>
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            gap: 2,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: theme.palette.text.primary,
                              lineHeight: 1.5,
                              flex: 1,
                            }}
                          >
                            {contentSnippet}
                          </Typography>

                          <Button
                            variant="ghost"
                            size="sm"
                            endIcon={<ArrowRight size={16} />}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleResultClick(result);
                            }}
                            sx={{
                              flexShrink: 0,
                              alignSelf: "flex-start",
                            }}
                          >
                            Ver mensaje
                          </Button>
                        </Box>
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Stack>
        </>
      )}
    </UnifiedModal>
  );
}

export const MESSAGE_PAGE_SIZE = PAGE_SIZE;
